import React, { useEffect, useMemo, useRef, useState } from "react";
import { CopilotModalShell } from "../CopilotModalShell";

const MAX_LEN = 500;

function validateLocally(v) {
  if (v.length === 0) return "required";
  if (v.length > MAX_LEN) return `must be ≤ 500 characters`;
  if (v.includes("{") || v.includes("}")) return "may not contain '{' or '}'";
  return null;
}

/**
 * Param-input modal for a workflow with declared parameters.
 *
 * `serverErrors` (optional) — list of {path, code, message} that the server
 * returned for an earlier attempt (e.g. params containing '{' that local
 * validation missed). Mapped to inline messages on the matching fields.
 */
export function WorkflowParamModal({ workflow, onSubmit, onCancel, serverErrors }) {
  const params = workflow.parameters || [];

  const [values, setValues] = useState(() => {
    const v = {};
    for (const p of params) v[p.name] = p.example_value || "";
    return v;
  });
  const [touched, setTouched] = useState({});
  const firstInputRef = useRef(null);

  useEffect(() => {
    if (firstInputRef.current) firstInputRef.current.focus();
  }, []);

  const localErrors = useMemo(() => {
    const e = {};
    for (const p of params) {
      const err = validateLocally(values[p.name] || "");
      if (err) e[p.name] = err;
    }
    return e;
  }, [params, values]);

  // Build a map of server-error messages by param name. Cleared once the
  // user touches that field (so we don't show stale errors after edits).
  const serverErrorByParam = useMemo(() => {
    if (!serverErrors) return {};
    const out = {};
    for (const e of serverErrors) {
      const m = /^params\.(\w+)$/.exec(e.path || "");
      if (m) out[m[1]] = e.message || e.code;
    }
    return out;
  }, [serverErrors]);

  const canSubmit = Object.keys(localErrors).length === 0;

  const handleChange = (name) => (ev) => {
    setValues((s) => ({ ...s, [name]: ev.target.value }));
    setTouched((s) => ({ ...s, [name]: true }));
  };

  const handleKeyDown = (ev) => {
    if (ev.key === "Enter" && canSubmit) {
      ev.preventDefault();
      onSubmit({ ...values });
    } else if (ev.key === "Escape") {
      ev.preventDefault();
      onCancel();
    }
  };

  return (
    <CopilotModalShell
      title={`Run: ${workflow.name}`}
      onClose={onCancel}
      footer={
        <>
          <button
            type="button"
            className="copilot-modal-btn copilot-modal-btn--ghost"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="copilot-modal-btn copilot-modal-btn--primary"
            disabled={!canSubmit}
            onClick={() => canSubmit && onSubmit({ ...values })}
          >
            Run workflow
          </button>
        </>
      }
    >
      {workflow.summary && (
        <p className="copilot-modal-body">{workflow.summary}</p>
      )}
      <div className="copilot-workflow-params">
        {params.map((p, i) => {
          const localErr = localErrors[p.name];
          const serverErr = !touched[p.name] && serverErrorByParam[p.name];
          const showErr = (touched[p.name] && localErr) || serverErr;
          return (
            <label key={p.name} className="copilot-workflow-param">
              <span className="copilot-workflow-param-name">{p.name}</span>
              <input
                ref={i === 0 ? firstInputRef : null}
                type="text"
                value={values[p.name] || ""}
                onChange={handleChange(p.name)}
                onKeyDown={handleKeyDown}
                aria-label={p.name}
                data-error={showErr ? "true" : "false"}
              />
              {p.description && (
                <span className="copilot-workflow-param-desc">{p.description}</span>
              )}
              {showErr && (
                <span className="copilot-workflow-param-error">{localErr || serverErr}</span>
              )}
            </label>
          );
        })}
      </div>
    </CopilotModalShell>
  );
}
